"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";

import { cn } from "@/libs/utils";
import { cva, VariantProps } from "class-variance-authority";
import ChevronUpIcon from "@/v2-components/core/icon/chevron-up-icon";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import CheckIcon from "@/v2-components/core/icon/check-icon";
import { Slot } from "@radix-ui/react-slot";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const selectTriggerVariants = cva(
  [
    "flex items-center justify-between select-none w-full text-coolgray-90 -outline-offset-2",
    "placeholder:text-coolgray-90 text-start",
    "disabled:pointer-events-none disabled:text-coolgray-30",
    "aria-[invalid=true]:outline  aria-[invalid=true]:outline-2 aria-[invalid=true]:outline-red-60",
  ],
  {
    variants: {
      variant: {
        form: [
          "h-12 rounded px-4 border border-coolgray-20 bg-white text-base",
          "[&>span]:line-clamp-1 [&>svg]:data-[state=open]:-rotate-180 [&>svg]:transition-transform transition-colors",
          "ring-offset-white focus:outline focus:outline-2 focus:outline-blue-60 ",
          "enabled:hover:bg-coolgray-10",
        ],
      },
    },
  },
);

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants>
>(({ className, variant, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ variant }), className)}
    {...props}
  >
    {children}
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUpIcon className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDownIcon className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const selectContentVariants = cva(
  [
    "relative z-50 max-h-96 min-w-[8rem] rounded",
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  ],
  {
    variants: {
      variant: {
        form: "ring-1 ring-coolgray-20 bg-white text-coolgray-90 shadow-md py-4",
      },
    },
    defaultVariants: {
      variant: "form",
    },
  },
);

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> &
    VariantProps<typeof selectContentVariants> & {
      portal?: boolean;
    }
>(
  (
    {
      className,
      variant,
      children,
      portal = true,
      position = "popper",
      ...props
    },
    ref,
  ) => {
    const Comp = portal ? SelectPrimitive.Portal : Slot;
    return (
      <Comp>
        <SelectPrimitive.Content
          ref={ref}
          className={cn(
            selectContentVariants({ variant }),
            position === "popper" &&
              "data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1",
            className,
          )}
          position={position}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              position === "popper" &&
                "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </Comp>
    );
  },
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-600", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const selectItemVariants = cva(
  [
    "text-coolgray-90 text-base font-400",
    "hover:bg-coolgray-10 focus:bg-coolgray-10 focus-visible:bg-coolgray-10",
    "data-[disabled]:pointer-events-none data-[disabled]:text-coolgray-30",
    "cursor-default select-none w-full relative flex items-center outline-none",
  ],
  {
    variants: {
      variant: {
        form: "px-4 py-[15px]",
      },
    },
    defaultVariants: {
      variant: "form",
    },
  },
);

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> &
    VariantProps<typeof selectItemVariants>
>(({ className, variant, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(selectItemVariants({ variant }), className)}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="absolute right-5 flex items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => {
  return (
    <div className={cn("px-4")}>
      <SelectPrimitive.Separator
        ref={ref}
        className={cn("-mx-1 -my-[1px] h-px w-full bg-coolgray-10", className)}
        {...props}
      />
    </div>
  );
});
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  selectTriggerVariants,
  selectContentVariants,
  selectItemVariants,
};
